import { csvNumber, show, span, unitsOf, yearLabel, yearLong } from "./format";
import type { Grid, Mapping, Metric, MetricAtPlace, Point, Summary, ValueType } from "./types";

/*
 * The climate metrics dashboard's rules, apart from drawing: which hazard and metric open first,
 * where a metric is available, the numbers each location shows, and the table built from them.
 */

export const MAX_POINTS = 30;
export const FULL_MESSAGE = `Up to ${MAX_POINTS} locations can be compared at once. Remove one to add another.`;
export const SCENARIO_COLOURS: Record<string, { light: string; dark: string }> = {
  ssp126: { light: "#173c66", dark: "#5b8fd0" },
  ssp245: { light: "#f79420", dark: "#f79420" },
  ssp370: { light: "#e71d25", dark: "#f0474d" },
  ssp585: { light: "#951b1e", dark: "#c43a3e" },
};
/** ESRS E1 AR 11 order: the four hazard classes, chronic before acute. */
const CLASS_ORDER = ["temperature", "wind", "water", "solid mass"];
const TYPE_ORDER = ["chronic", "acute"];
const OPENING_HAZARD = "Changing temperature (air, freshwater, marine water)";
const OPENING_SCENARIO = "ssp245";
/** A change map at the baseline is zero everywhere, so a change opens on mid-century. */
const CHANGE_CENTRE = 2050;

export type Layout = "year" | "ssp" | "all";

/** What the user is looking at. */
export interface Selection {
  hazard: string;
  metric: string;
  vt: ValueType;
  /** The map's scenario. */
  scen: string;
  /** The map's horizon, an index into summary.horizons; 0 is the baseline. */
  h: number;
  points: Point[];
  /** The location the chart shows, an index into points; -1 for none. */
  sel: number;
  layout: Layout;
  /** The table's own scenario (locations × years) and horizon (locations × scenarios). */
  tScen: string;
  tH: number;
}

export const hasVT = (m: Metric, vt: ValueType) => !!m.scales[vt];
/** Whether metric `m` has a map under scenario `s` at horizon index `k`. */
export const pairOK = (m: Metric, s: string, k: number) => {
  const p = m.pairs[s];
  return !!p && p.status !== "not_published" && p.status !== "pending" && !!p.horizons[k];
};
export const scenAvailable = (summary: Summary, m: Metric, s: string) =>
  summary.horizons.some((_, k) => k > 0 && pairOK(m, s, k));

export const metricOf = (summary: Summary, id: string) => summary.metrics.find((m) => m.id === id)!;
export const hazardsOf = (summary: Summary) => [...new Set(summary.mappings.map((r) => r.esrs_hazard))];
export const rowsOf = (summary: Summary, hazard: string) => summary.mappings.filter((r) => r.esrs_hazard === hazard);
export const shortHazard = (hazard: string) => hazard.replace(/\s*\(.*\)\s*$/, "");

/** The hazards grouped for the picker: by ESRS class then type, in AR 11 order. */
export function hazardGroups(summary: Summary): { label: string; hazards: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const h of hazardsOf(summary)) {
    const r = rowsOf(summary, h)[0];
    const key = `${r.hazard_class}|${r.hazard_type}`;
    groups.set(key, [...(groups.get(key) ?? []), h]);
  }
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return CLASS_ORDER.flatMap((c) =>
    TYPE_ORDER.flatMap((t) => {
      const hazards = groups.get(`${c}|${t}`);
      return hazards ? [{ label: `${cap(c)} · ${t}`, hazards }] : [];
    })
  );
}

/** A hazard's metrics, its default first. */
export function metricRows(summary: Summary, hazard: string): Mapping[] {
  const def = summary.hazardDefaults[hazard];
  return rowsOf(summary, hazard).sort((a, b) => Number(b.metric_id === def) - Number(a.metric_id === def));
}

const centreIndex = (summary: Summary) => {
  const k = summary.horizons.findIndex((h) => h.centre === CHANGE_CENTRE);
  return k < 0 ? Math.min(1, summary.horizons.length - 1) : k;
};
const defaultMetric = (summary: Summary, hazard: string) =>
  summary.hazardDefaults[hazard] ?? rowsOf(summary, hazard)[0].metric_id;

export function initialSelection(summary: Summary): Selection {
  const hazards = hazardsOf(summary);
  const hazard = hazards.includes(OPENING_HAZARD) ? OPENING_HAZARD : hazards[0];
  const ids = summary.scenarios.map((s) => s.id);
  const scen = ids.includes(OPENING_SCENARIO) ? OPENING_SCENARIO : ids[0];
  return {
    hazard,
    metric: defaultMetric(summary, hazard),
    vt: "value",
    scen,
    h: 0,
    points: [],
    sel: -1,
    layout: "year",
    tScen: scen,
    tH: centreIndex(summary),
  };
}

/**
 * The selection made consistent with the build and its metric, as the dashboard does whenever
 * the metric changes: a hazard or metric the build lacks falls back to the defaults, a value type
 * the metric does not publish to the other, a change at the baseline moves to mid-century, and a
 * scenario without the metric to the first that has it. Returns the same object when nothing
 * changes.
 */
export function normalise(summary: Summary, s: Selection): Selection {
  const fix: Partial<Selection> = {};
  const hazard = hazardsOf(summary).includes(s.hazard) ? s.hazard : initialSelection(summary).hazard;
  if (hazard !== s.hazard) fix.hazard = hazard;
  const metric = rowsOf(summary, hazard).some((r) => r.metric_id === s.metric)
    ? s.metric
    : defaultMetric(summary, hazard);
  if (metric !== s.metric) fix.metric = metric;
  const m = metricOf(summary, metric);
  const vt = hasVT(m, s.vt) ? s.vt : hasVT(m, "value") ? "value" : "diff";
  if (vt !== s.vt) fix.vt = vt;
  const inRange = (k: number) => Number.isInteger(k) && k >= 0 && k < summary.horizons.length;
  let h = inRange(s.h) ? s.h : 0;
  if (vt === "diff" && h === 0) h = centreIndex(summary);
  if (h !== s.h) fix.h = h;
  const first = summary.scenarios.find((x) => scenAvailable(summary, m, x.id))?.id;
  const scen = scenAvailable(summary, m, s.scen) ? s.scen : (first ?? summary.scenarios[0].id);
  if (scen !== s.scen) fix.scen = scen;
  const tScen = scenAvailable(summary, m, s.tScen) ? s.tScen : scen;
  if (tScen !== s.tScen) fix.tScen = tScen;
  const tH = inRange(s.tH) && s.tH > 0 ? s.tH : centreIndex(summary);
  if (tH !== s.tH) fix.tH = tH;
  const points = s.points.slice(0, MAX_POINTS);
  if (points.length !== s.points.length) fix.points = points;
  const sel = points.length ? Math.min(Math.max(s.sel, 0), points.length - 1) : -1;
  if (sel !== s.sel) fix.sel = sel;
  return Object.keys(fix).length ? { ...s, ...fix } : s;
}

/** Appends a location and selects it. The map drops double-clicks itself, through `singleClicks`. */
export function addPoint(s: Selection, point: Point): Selection {
  return { ...s, points: [...s.points, point], sel: s.points.length };
}

/**
 * Tells a map click from the later clicks of a double-click or double-tap, which zoom the map and
 * should not add a location. The browser counts a double-click in the click's `detail`; where it
 * does not count touch taps, Leaflet takes two taps within 200 ms for a double-tap, and so does this.
 */
export function singleClicks() {
  let last = -Infinity;
  return (detail: number, at: number) => {
    const since = at - last;
    last = at;
    return detail < 2 && since > 200;
  };
}

/** The change the dashboard makes when the user picks a hazard: its default metric. */
export const pickHazard = (summary: Summary, s: Selection, hazard: string): Selection =>
  normalise(summary, { ...s, hazard, metric: defaultMetric(summary, hazard) });

/** Picking "change from baseline" at the baseline steps the slider to mid-century. */
export const pickValueType = (summary: Summary, s: Selection, vt: ValueType): Selection =>
  normalise(summary, { ...s, vt, h: vt === "diff" && s.h === 0 ? centreIndex(summary) : s.h });

/** The scenario whose baseline layer stands for every scenario's: the first that has one. */
export const baselineScenario = (summary: Summary, m: Metric) =>
  summary.scenarios.find((x) => m.pairs[x.id]?.horizons[0])?.id ?? null;

/** Why the map is empty for this selection, or "" when there is a map. */
export function mapReason(summary: Summary, m: Metric, s: Selection): string {
  if (s.h === 0 && s.vt === "diff")
    return "At the baseline the change is zero by definition. Move the time slider to a later year.";
  if (s.h === 0 && !hasVT(m, "value"))
    return "This metric is published as a change from the baseline only, so there is no baseline map.";
  if (s.h > 0 && !pairOK(m, s.scen, s.h)) {
    const code = scenarioLabel(summary, s.scen);
    return `Not available under ${code} for this metric${scenAvailable(summary, m, s.scen) ? " at this time slice" : ""}.`;
  }
  return "";
}

export const scenarioLabel = (summary: Summary, id: string) =>
  (summary.scenarios.find((s) => s.id === id)?.label ?? id).split(" ·")[0];

/** What the definition panel says about where the metric is missing. */
export function availabilityNote(summary: Summary, m: Metric): string {
  const missing = summary.scenarios
    .filter((s) => !scenAvailable(summary, m, s.id))
    .map((s) => scenarioLabel(summary, s.id));
  const partial = summary.scenarios.filter(
    (s) => scenAvailable(summary, m, s.id) && summary.horizons.some((_, k) => k > 0 && !pairOK(m, s.id, k))
  );
  return [
    missing.length ? `Not available under ${missing.join(", ")}.` : "",
    ...partial.map((s) => {
      const first = summary.horizons.find((_, k) => k > 0 && pairOK(m, s.id, k))!;
      return `Under ${scenarioLabel(summary, s.id)} it is available from ${first.centre} only.`;
    }),
    hasVT(m, "value") ? "" : "Published as a change from the baseline only.",
  ]
    .filter(Boolean)
    .join(" ");
}

/** One location's numbers: for each scenario, a value per horizon (null where none). */
export type Series = Record<string, (number | null)[]>;

/**
 * A location's values of metric `m` in value type `vt`, as the dashboard reads them. The baseline
 * is the same in every scenario, so it comes from the baseline scenario; a change is zero there.
 * A horizon without a map under a scenario has no value.
 */
export function seriesOf(summary: Summary, m: Metric, vt: ValueType, at: MetricAtPlace | null | undefined): Series {
  const base = baselineScenario(summary, m);
  const b = base === null ? -1 : summary.scenarios.findIndex((x) => x.id === base);
  const out: Series = {};
  summary.scenarios.forEach(({ id }, s) => {
    out[id] = summary.horizons.map((_, k) => {
      if (!at) return null;
      if (k === 0) {
        if (vt === "diff") return hasVT(m, "diff") ? 0 : null;
        return hasVT(m, "value") && b >= 0 ? at.value[0][b] : null;
      }
      if (!pairOK(m, id, k) || !hasVT(m, vt)) return null;
      return at[vt][k][s];
    });
  });
  return out;
}

export interface TableRow {
  /** The location's index in the selection. */
  k: number;
  point: Point;
  scenario?: string;
  /** Read from a neighbouring cell. */
  moved: boolean;
  lead: string[];
  numbers: (number | null)[];
}

export interface TableModel {
  caption: string;
  head: string[];
  nLead: number;
  rows: TableRow[];
}

export interface Sort {
  col: number | null;
  dir: 1 | -1;
}

/**
 * The table the page shows and the CSV holds: locations by years for one scenario, by scenarios
 * for one year (with the baseline), or by years for every scenario. The table has its own
 * scenario and year, independent of the map. A location with no value keeps its row.
 */
export function tableModel(
  summary: Summary,
  s: Selection,
  series: (Series | null)[],
  cells: ({ moved: boolean } | null)[],
  sort: Sort
): TableModel {
  const m = metricOf(summary, s.metric);
  const H = summary.horizons;
  const units = unitsOf(m);
  const what = `${m.plain_label}${units ? ` (${units})` : ""}, ${s.vt === "diff" ? "change from baseline" : "raw value"}`;
  const avg = "Each year is the average of the 20 years around it.";
  const withBase = s.vt === "value" && hasVT(m, "value");
  const ks = H.map((_, k) => k).filter((k) => k > 0 || withBase);
  let lead = ["#", "Location", "Latitude", "Longitude"];
  let cols: string[];
  let caption: string;
  let lines: { k: number; scenario?: string; get: (x: Series) => (number | null)[] }[];
  if (s.layout === "year") {
    cols = ks.map((k) => yearLabel(H[k]));
    caption = `${what}, ${scenarioLabel(summary, s.tScen)}. ${avg}`;
    lines = s.points.map((_, k) => ({ k, get: (x) => ks.map((j) => x[s.tScen][j]) }));
  } else if (s.layout === "ssp") {
    const ids = summary.scenarios.map((x) => x.id);
    cols = [...(withBase ? ["Baseline"] : []), ...ids.map((id) => scenarioLabel(summary, id))];
    caption = `${what}, ${yearLong(H[s.tH])}${withBase ? `, with the ${span(H[0])} baseline` : ""}.`;
    lines = s.points.map((_, k) => ({
      k,
      get: (x) => [...(withBase ? [x[ids[0]][0]] : []), ...ids.map((id) => x[id][s.tH])],
    }));
  } else {
    lead = [...lead, "Scenario"];
    cols = ks.map((k) => yearLabel(H[k]));
    caption = `${what}, every scenario. ${avg}`;
    const available = summary.scenarios.map((x) => x.id).filter((id) => scenAvailable(summary, m, id));
    lines = s.points.flatMap((_, k) =>
      available.map((id) => ({ k, scenario: id, get: (x: Series) => ks.map((j) => x[id][j]) }))
    );
  }
  let rows: TableRow[] = lines.map(({ k, scenario, get }) => {
    const point = s.points[k];
    const x = series[k];
    return {
      k,
      point,
      scenario,
      moved: !!cells[k]?.moved,
      lead: [
        String(k + 1),
        point.name,
        point.lat.toFixed(3),
        point.lon.toFixed(3),
        ...(scenario ? [scenarioLabel(summary, scenario)] : []),
      ],
      numbers: x ? get(x) : cols.map(() => null),
    };
  });
  const c = sort.col;
  if (c !== null) {
    const nLead = lead.length;
    const key = (r: TableRow): number | string | null =>
      c === 0
        ? r.k
        : c === 1
          ? r.point.name.toLowerCase()
          : c === 2
            ? r.point.lat
            : c === 3
              ? r.point.lon
              : c < nLead
                ? r.lead[c]
                : r.numbers[c - nLead];
    rows = [...rows].sort((a, b) => {
      const va = key(a);
      const vb = key(b);
      // A row without a value always sorts last.
      if (va === null) return vb === null ? 0 : 1;
      if (vb === null) return -1;
      return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
    });
  }
  return { caption, head: [...lead, ...cols], nLead: lead.length, rows };
}

/** A table cell as the page shows it. */
export const cellText = (v: number | null, vt: ValueType) => (v === null ? "—" : show(v, vt));

/** The CSV: the table's header and rows, numbers at full precision. */
export const csvRows = (t: TableModel): string[][] => [
  t.head,
  ...t.rows.map((r) => [...r.lead, ...r.numbers.map(csvNumber)]),
];

export function csvName(summary: Summary, s: Selection): string {
  const tag =
    s.layout === "year" ? s.tScen : s.layout === "ssp" ? String(summary.horizons[s.tH].centre) : "all_scenarios";
  return `${s.metric}_${s.vt === "diff" ? "change" : "raw"}_${tag}.csv`;
}

/** The chart's y range: from zero unless zero is an arbitrary point (°C, pH), padded away from zero. */
const ARBITRARY_ZERO = new Set(["annual_mean_tas", "surface_ph"]);
export function chartRange(metricId: string, vt: ValueType, values: number[]): [number, number] {
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  const fromZero = vt === "diff" || !ARBITRARY_ZERO.has(metricId);
  if (fromZero) {
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  if (hi - lo < 1e-9) {
    lo -= 1;
    hi += 1;
  }
  const pad = (hi - lo) * 0.08;
  return [fromZero && lo === 0 ? 0 : lo - pad, fromZero && hi === 0 ? 0 : hi + pad];
}

/**
 * The cell holding (lat, lon): its row counted from the south, as the cube stores rows, and
 * column. Rows are found from the north edge down, dividing by the grid's span before
 * multiplying by its cells as the dashboard does, so a point on a cell edge falls in the cell
 * the dashboard reads. Longitude wraps; null outside the grid's rows.
 */
export function cellOf(
  lat: number,
  lon: number,
  { lat0, lon0, step, rows, cols }: Grid
): { i: number; j: number } | null {
  const north = lat0 + (rows - 0.5) * step;
  const west = lon0 - step / 2;
  // A point on the grid's south or east edge is in its last row or column, as the Worker clamps it.
  const r = lat === north - rows * step ? rows - 1 : Math.floor(((north - lat) / (rows * step)) * rows);
  if (r < 0 || r >= rows) return null;
  const j = lon === west + cols * step ? cols - 1 : Math.floor(((lon - west) / (cols * step)) * cols);
  return { i: rows - 1 - r, j: ((j % cols) + cols) % cols };
}
